import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogType?: string;
  noIndex?: boolean;
  isHomepage?: boolean;
}

const getBaseUrl = () => {
  // Use environment variable first
  const envUrl = import.meta.env.VITE_APP_URL;
  if (envUrl) return envUrl;

  // Fallback to strict production URL if not found (safer for canonicals)
  return 'https://vibetools.online';
};

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogImageAlt,
  ogType = 'website',
  noIndex = false,
  isHomepage = false,
}: SEOHeadProps) {
  const baseUrl = getBaseUrl();
  const fullTitle = title;

  const getAbsoluteUrl = (path?: string) => {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${cleanPath}`;
  };

  // Fix: dynamic fallback to current window pathname if canonicalUrl is not provided
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const fullCanonicalUrl = isHomepage
    ? baseUrl
    : canonicalUrl
      ? getAbsoluteUrl(canonicalUrl)
      : getAbsoluteUrl(currentPath);

  const defaultOgImage = ogImage
    ? getAbsoluteUrl(ogImage)
    : `${baseUrl}/og-image.png`;

  const finalOgImageAlt = ogImageAlt || 'VibeTools — Free Online Tools';

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullCanonicalUrl} />

      {/* Robots */}
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />
      <meta name="googlebot" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={defaultOgImage} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={finalOgImageAlt} />
      <meta property="og:site_name" content="VibeTools" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@VibeTools" />
      <meta name="twitter:creator" content="@VibeTools" />
      <meta name="twitter:url" content={fullCanonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={defaultOgImage} />
      <meta name="twitter:image:alt" content={finalOgImageAlt} />
    </Helmet>
  );
}
